import styled from "@emotion/styled";
import {
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Theme,
  useMediaQuery
} from "@mui/material";
import type { BreadcrumbData } from "@ui/atoms/Breadcrumbs/Breadcrumbs";
import { Fragment, KeyboardEvent, useRef, useState } from "react";
import { Link } from "react-router-dom";

export const CrumbScope = styled("span")(({ theme }) => ({
  color: theme.vars.ui.topBar.crumbScope,
  flexShrink: 1,
  lineHeight: 1.2,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
}));

export const CrumbSeparator = styled("span")(({ theme }) => ({
  color: theme.vars.ui.topBar.separator,
  flexShrink: 0
}));

export const CrumbSection = styled("span")(({ theme }) => ({
  color: theme.vars.ui.topBar.crumbSection,
  // The section is the crumb that says where you are, so it is the last thing
  // to give: the scope beside it names the project or organisation the nav is
  // already showing. Both shrinking together got "MULTIPL... / OVERVIE...",
  // where all that was needed was for the first one to yield.
  flexShrink: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
}));

const interactiveCrumb = (theme: Theme) => ({
  "&:focus-visible": {
    borderRadius: 2,
    outline: `1px solid ${theme.vars.ui.topBar.lineHover}`,
    outlineOffset: 2
  },
  "&:hover": {
    color: theme.vars.ui.topBar.crumbSection
  },
  background: "none",
  border: "none",
  color: theme.vars.ui.topBar.crumbScope,
  cursor: "pointer",
  flexShrink: 1,
  font: "inherit",
  letterSpacing: "inherit",
  lineHeight: 1.2,
  minWidth: 0,
  overflow: "hidden",
  padding: 0,
  textDecoration: "none",
  textOverflow: "ellipsis",
  textTransform: "inherit" as const,
  whiteSpace: "nowrap"
});

const CrumbLink = styled(Link)(({ theme }) => interactiveCrumb(theme));

const CrumbButton = styled("button")(({ theme }) => interactiveCrumb(theme));

const MoreButton = styled("button")(({ theme }) => ({
  ...interactiveCrumb(theme),
  flexShrink: 0
}));

const TrailGroup = styled("span")({
  display: "contents"
});

const Separator = () => <CrumbSeparator aria-hidden>/</CrumbSeparator>;

const Crumb = ({ crumb }: { crumb: BreadcrumbData }) => {
  if (crumb.href) {
    return (
      <CrumbLink data-test-id="breadcrumb-item" to={crumb.href}>
        {crumb.label}
      </CrumbLink>
    );
  }

  if (crumb.action) {
    return (
      <CrumbButton
        data-test-id="breadcrumb-item"
        onClick={crumb.action}
        type="button"
      >
        {crumb.label}
      </CrumbButton>
    );
  }

  return <CrumbScope data-test-id="breadcrumb-item">{crumb.label}</CrumbScope>;
};

const HiddenCrumbsMenu = ({ crumbs }: { crumbs: BreadcrumbData[] }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const close = () => {
    setOpen(false);
    anchorRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  };

  return (
    <>
      <MoreButton
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Show hidden breadcrumbs"
        data-test-id="breadcrumb-more-button"
        onClick={() => setOpen((prev) => !prev)}
        ref={anchorRef}
        type="button"
      >
        …
      </MoreButton>

      <Popper
        anchorEl={anchorRef.current}
        modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
        open={open}
        placement="bottom-start"
        sx={(theme) => ({ zIndex: theme.zIndex.modal })}
        transition
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
            <Paper elevation={2} sx={{ px: 1 }}>
              <ClickAwayListener
                onClickAway={(event) => {
                  if (anchorRef.current?.contains(event.target as Node)) {
                    return;
                  }
                  setOpen(false);
                }}
              >
                <MenuList autoFocusItem={open} onKeyDown={handleKeyDown}>
                  {crumbs.map((crumb, index) =>
                    crumb.href ? (
                      <MenuItem
                        component={Link}
                        data-test-id="breadcrumb-menu-item"
                        key={`${index}-${crumb.label}-${crumb.href ?? ""}`}
                        onClick={() => setOpen(false)}
                        to={crumb.href}
                      >
                        {crumb.label}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        data-test-id="breadcrumb-menu-item"
                        disabled={!crumb.action}
                        key={`${index}-${crumb.label}-${crumb.href ?? ""}`}
                        onClick={() => {
                          crumb.action?.();
                          close();
                        }}
                      >
                        {crumb.label}
                      </MenuItem>
                    )
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export const AppTopBarTrail = ({ trail }: { trail: BreadcrumbData[] }) => {
  const isConstrained = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("lg")
  );

  const last = trail[trail.length - 1];
  const ancestors = trail.slice(0, -1);
  const collapsible = ancestors.slice(1);
  const collapse = isConstrained && collapsible.length > 0;
  const visibleAncestors = collapse ? ancestors.slice(0, 1) : ancestors;

  return (
    <TrailGroup data-test-id="breadcrumb-group">
      {visibleAncestors.map((crumb, index) => (
        <Fragment key={`${index}-${crumb.label}-${crumb.href ?? ""}`}>
          <Separator />
          <Crumb crumb={crumb} />
        </Fragment>
      ))}

      {collapse && (
        <>
          <Separator />
          <HiddenCrumbsMenu crumbs={collapsible} />
        </>
      )}

      <Separator />

      <CrumbSection aria-current="page" data-test-id="breadcrumb-item">
        {last.label}
      </CrumbSection>
    </TrailGroup>
  );
};
